import { useState, useEffect, useRef, useCallback } from 'react';
import apiClient from '../utils/api';
import { playMessageNotificationSound } from '../utils/messageNotificationSound';

const POLL_MS = 30000;

export default function useAdminDocumentNotifications({
  enabled = false,
  addToast,
  suppressAlerts = false,
}) {
  const [openCount, setOpenCount] = useState(0);
  const [stats, setStats] = useState({ pendingDocs: 0, pendingAccessRequests: 0, open: 0 });
  const lastAlertedCountRef = useRef(null);
  const primedRef = useRef(false);
  const addToastRef = useRef(addToast);
  const suppressAlertsRef = useRef(suppressAlerts);

  useEffect(() => {
    addToastRef.current = addToast;
  }, [addToast]);

  useEffect(() => {
    suppressAlertsRef.current = suppressAlerts;
  }, [suppressAlerts]);

  const refreshOpenCount = useCallback(async ({ silent = false } = {}) => {
    if (!enabled) {
      setOpenCount(0);
      setStats({ pendingDocs: 0, pendingAccessRequests: 0, open: 0 });
      lastAlertedCountRef.current = 0;
      return 0;
    }
    try {
      const res = await apiClient.getPropertyDocumentQueueStats();
      const nextStats = res.data || {};
      const next = Number(nextStats.open) || 0;
      const lastAlerted = lastAlertedCountRef.current;

      if (!primedRef.current) {
        primedRef.current = true;
        lastAlertedCountRef.current = next;
        setOpenCount(next);
        setStats({
          pendingDocs: Number(nextStats.pendingDocs) || 0,
          pendingAccessRequests: Number(nextStats.pendingAccessRequests) || 0,
          open: next,
        });
        return next;
      }

      const hasNew = lastAlerted !== null && next > lastAlerted;
      const shouldNotify = !silent && hasNew && !suppressAlertsRef.current;

      if (shouldNotify) {
        const delta = next - lastAlerted;
        const label = delta === 1
          ? 'New property document needs review'
          : `${delta} property documents need review`;
        addToastRef.current?.(label, 'info');
        if (!document.hidden) playMessageNotificationSound();
        lastAlertedCountRef.current = next;
      } else if (suppressAlertsRef.current && hasNew) {
        lastAlertedCountRef.current = next;
      } else if (lastAlerted !== null && next < lastAlerted) {
        lastAlertedCountRef.current = next;
      }

      setOpenCount(next);
      setStats({
        pendingDocs: Number(nextStats.pendingDocs) || 0,
        pendingAccessRequests: Number(nextStats.pendingAccessRequests) || 0,
        open: next,
      });
      return next;
    } catch (_) {
      return lastAlertedCountRef.current ?? 0;
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      setOpenCount(0);
      setStats({ pendingDocs: 0, pendingAccessRequests: 0, open: 0 });
      lastAlertedCountRef.current = 0;
      primedRef.current = false;
      return undefined;
    }

    refreshOpenCount({ silent: true });
    const id = window.setInterval(() => refreshOpenCount({ silent: false }), POLL_MS);
    return () => window.clearInterval(id);
  }, [enabled, refreshOpenCount]);

  return { openCount, stats, refreshOpenCount };
}
